import React, { useCallback, useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { ThemedButton, ThemedCard, ThemedInput } from '../themed';

const DAY_OPTIONS = [
  { value: 0, labelKey: 'sunday' },
  { value: 1, labelKey: 'monday' },
  { value: 2, labelKey: 'tuesday' },
  { value: 3, labelKey: 'wednesday' },
  { value: 4, labelKey: 'thursday' },
  { value: 5, labelKey: 'friday' },
  { value: 6, labelKey: 'saturday' },
];

const createEmptyPolicy = () => ({
  enabled: false,
  daysOfWeek: [],
  startTime: '',
  endTime: '',
  minRiders: '',
  maxRiders: '',
  minLuggages: '',
  maxLuggages: '',
  routeIds: [],
  limitToSelectedRoutes: false,
});

const normalizePolicyResponse = (data) => ({
  enabled: Boolean(data?.enabled),
  daysOfWeek: Array.isArray(data?.daysOfWeek) ? data.daysOfWeek : [],
  startTime: data?.startTime || '',
  endTime: data?.endTime || '',
  minRiders: data?.minRiders ?? '',
  maxRiders: data?.maxRiders ?? '',
  minLuggages: data?.minLuggages ?? '',
  maxLuggages: data?.maxLuggages ?? '',
  routeIds: Array.isArray(data?.routeIds) ? data.routeIds : [],
  limitToSelectedRoutes: Array.isArray(data?.routeIds) && data.routeIds.length > 0,
});

const VehicleAssignmentPolicyPanel = ({ entityId, vehicleId, vehicle }) => {
  const { t } = useTranslation();
  const { background, border, text, components, cn } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [routes, setRoutes] = useState([]);
  const [mode, setMode] = useState('manual_only');
  const [policy, setPolicy] = useState(createEmptyPolicy());

  const loadData = useCallback(async () => {
    if (!entityId || !vehicleId) {
      setPolicy(createEmptyPolicy());
      setMode('manual_only');
      setRoutes([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [settingsResponse, policyResponse, routesResponse] = await Promise.all([
        axios.get(`/api/vehicles/${entityId}/${vehicleId}/assignment-settings`),
        axios.get(`/api/vehicles/${entityId}/${vehicleId}/assignment-policy`),
        axios.get(`/api/routes/${entityId}`),
      ]);

      setMode(settingsResponse?.data?.data?.mode || 'manual_only');
      setPolicy(normalizePolicyResponse(policyResponse?.data?.data));
      setRoutes(Array.isArray(routesResponse?.data) ? routesResponse.data : []);
    } catch {
      toast.error(t('vehicleAssignment.failedToLoad'));
      setPolicy(createEmptyPolicy());
      setRoutes([]);
    } finally {
      setLoading(false);
    }
  }, [entityId, vehicleId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updatePolicyField = useCallback((field, value) => {
    setPolicy((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const toggleDay = useCallback((dayValue) => {
    setPolicy((prev) => {
      const nextDays = prev.daysOfWeek.includes(dayValue)
        ? prev.daysOfWeek.filter((value) => value !== dayValue)
        : [...prev.daysOfWeek, dayValue].sort((a, b) => a - b);

      return {
        ...prev,
        daysOfWeek: nextDays,
      };
    });
  }, []);

  const toggleRoute = useCallback((routeId) => {
    setPolicy((prev) => {
      const nextRouteIds = prev.routeIds.includes(routeId)
        ? prev.routeIds.filter((value) => value !== routeId)
        : [...prev.routeIds, routeId].sort((a, b) => a - b);

      return {
        ...prev,
        routeIds: nextRouteIds,
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!entityId || !vehicleId) {
      return;
    }

    const payload = {
      enabled: policy.enabled,
      daysOfWeek: policy.daysOfWeek,
      startTime: policy.startTime || null,
      endTime: policy.endTime || null,
      minRiders: policy.minRiders === '' ? null : Number(policy.minRiders),
      maxRiders: policy.maxRiders === '' ? null : Number(policy.maxRiders),
      minLuggages: policy.minLuggages === '' ? null : Number(policy.minLuggages),
      maxLuggages: policy.maxLuggages === '' ? null : Number(policy.maxLuggages),
      routeIds: policy.limitToSelectedRoutes ? policy.routeIds : [],
    };

    try {
      setSaving(true);
      await axios.put(`/api/vehicles/${entityId}/${vehicleId}/assignment-settings`, { mode });
      const response = await axios.put(`/api/vehicles/${entityId}/${vehicleId}/assignment-policy`, payload);
      setPolicy(normalizePolicyResponse(response?.data?.data));
      toast.success(t('vehicleAssignment.settingsSaved'));
    } catch (error) {
      const backendError = error?.response?.data?.error;
      toast.error(backendError || t('vehicleAssignment.failedToSave'));
    } finally {
      setSaving(false);
    }
  }, [entityId, mode, policy, t, vehicleId]);

  if (loading) {
    return (
      <div className={cn('p-6 text-center', text.muted)}>
        {t('loading')}...
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', background.secondary)}>
      <ThemedCard>
        <ThemedCard.Header>
          <div>
            <div className={cn('text-lg font-semibold', text.primary)}>
              {vehicle?.vehicle_number || t('vehicleAssignment.policyTab')}
            </div>
            <div className={cn('text-sm mt-1', text.muted)}>
              {t('vehicleAssignment.policyHelp')}
            </div>
          </div>
        </ThemedCard.Header>
        <ThemedCard.Body>
          <div className="space-y-6">
            <div>
              <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                {t('vehicleAssignment.modeLabel')}
              </label>
              <select
                value={mode}
                onChange={(event) => setMode(event.target.value)}
                className={cn('w-full px-3 py-2 border rounded-md', components.input.base, components.input.focus)}
              >
                <option value="manual_only">{t('vehicleAssignment.manualOnly')}</option>
                <option value="eligible">{t('vehicleAssignment.eligible')}</option>
                <option value="request_only">{t('vehicleAssignment.requestOnly')}</option>
              </select>
              <p className={cn('text-xs mt-2', text.muted)}>
                {t(`vehicleAssignment.modeDescriptions.${mode}`)}
              </p>
            </div>

            <div className={cn('pt-4 border-t space-y-4', border.default)}>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={policy.enabled}
                  onChange={(event) => updatePolicyField('enabled', event.target.checked)}
                  className="mt-1"
                />
                <div>
                  <div className={cn('text-sm font-medium', text.primary)}>
                    {t('vehicleAssignment.enablePolicy')}
                  </div>
                  <div className={cn('text-xs mt-1', text.muted)}>
                    {t('vehicleAssignment.enablePolicyHelp')}
                  </div>
                </div>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    {t('vehicleAssignment.startTime')}
                  </label>
                  <ThemedInput
                    type="time"
                    value={policy.startTime}
                    onChange={(event) => updatePolicyField('startTime', event.target.value)}
                  />
                </div>
                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    {t('vehicleAssignment.endTime')}
                  </label>
                  <ThemedInput
                    type="time"
                    value={policy.endTime}
                    onChange={(event) => updatePolicyField('endTime', event.target.value)}
                  />
                </div>
                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    {t('vehicleAssignment.minRiders')}
                  </label>
                  <ThemedInput
                    type="number"
                    min="0"
                    value={policy.minRiders}
                    onChange={(event) => updatePolicyField('minRiders', event.target.value)}
                  />
                </div>
                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    {t('vehicleAssignment.maxRiders')}
                  </label>
                  <ThemedInput
                    type="number"
                    min="0"
                    value={policy.maxRiders}
                    onChange={(event) => updatePolicyField('maxRiders', event.target.value)}
                  />
                </div>
                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    {t('vehicleAssignment.minLuggage')}
                  </label>
                  <ThemedInput
                    type="number"
                    min="0"
                    value={policy.minLuggages}
                    onChange={(event) => updatePolicyField('minLuggages', event.target.value)}
                  />
                </div>
                <div>
                  <label className={cn('block text-sm font-medium mb-2', text.secondary)}>
                    {t('vehicleAssignment.maxLuggage')}
                  </label>
                  <ThemedInput
                    type="number"
                    min="0"
                    value={policy.maxLuggages}
                    onChange={(event) => updatePolicyField('maxLuggages', event.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className={cn('block text-sm font-medium mb-2', text.secondary)}>
                  {t('vehicleAssignment.daysOfWeek')}
                </div>
                <div className="flex flex-wrap gap-2">
                  {DAY_OPTIONS.map((day) => {
                    const active = policy.daysOfWeek.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleDay(day.value)}
                        className={cn(
                          'px-3 py-1.5 rounded-full border text-sm transition-colors',
                          active
                            ? 'bg-blue-600 text-white border-blue-600'
                            : cn(border.default, text.secondary)
                        )}
                      >
                        {t(day.labelKey)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className={cn('pt-4 border-t', border.default)}>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={policy.limitToSelectedRoutes}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setPolicy((prev) => ({
                        ...prev,
                        limitToSelectedRoutes: checked,
                        routeIds: checked ? prev.routeIds : [],
                      }));
                    }}
                    className="mt-1"
                  />
                  <div>
                    <div className={cn('text-sm font-medium', text.primary)}>
                      {t('vehicleAssignment.selectedRoutes')}
                    </div>
                    <div className={cn('text-xs mt-1', text.muted)}>
                      {t('vehicleAssignment.selectedRoutesHelp')}
                    </div>
                  </div>
                </label>

                {policy.limitToSelectedRoutes && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                    {routes.map((route) => (
                      <label
                        key={route.id}
                        className={cn('flex items-center gap-2 px-3 py-2 rounded-md border', border.default)}
                      >
                        <input
                          type="checkbox"
                          checked={policy.routeIds.includes(route.id)}
                          onChange={() => toggleRoute(route.id)}
                        />
                        <span className={cn('text-sm', text.primary)}>{route.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ThemedCard.Body>
        <ThemedCard.Footer>
          <div className="flex justify-end">
            <ThemedButton onClick={handleSave} loading={saving} variant="primary">
              {t('save')}
            </ThemedButton>
          </div>
        </ThemedCard.Footer>
      </ThemedCard>
    </div>
  );
};

export default React.memo(VehicleAssignmentPolicyPanel);
